import { FC, memo } from 'react';

import { Ban, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';

import css from '../MailingCampaigns.module.css';

import { mailingService } from 'v2/api/mailingService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { MailingCampaign } from 'v2/types/MailingModel';
import { handleHttpError } from 'v2/utils';

interface CampaignActionsColumnProps {
  campaign: MailingCampaign;
  onRevalidate: VoidFunction;
}

export const CampaignActionsColumn: FC<CampaignActionsColumnProps> = memo(({ campaign, onRevalidate }) => {
  const { trigger: startCampaign } = useSWRMutation(
    ['startMailingCampaign', campaign.id],
    mailingService.startCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign started successfully');
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: cancelCampaign } = useSWRMutation(
    ['cancelMailingCampaign', campaign.id],
    mailingService.cancelCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign cancelled');
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: deleteCampaign } = useSWRMutation(
    ['deleteMailingCampaign', campaign.id],
    mailingService.deleteCampaign,
    {
      onSuccess: () => {
        toast.success('Campaign deleted');
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  return (
    <div className={css.actionsCell}>
      {campaign.status === 'PENDING' && (
        <>
          <Popconfirm
            message="Start campaign? This will begin sending messages to all recipients"
            onConfirm={startCampaign}
          >
            <IconButton size="small" color="primary">
              <Play size={16} />
            </IconButton>
          </Popconfirm>

          <Popconfirm message="Delete campaign? This action cannot be undone" onConfirm={deleteCampaign}>
            <IconButton size="small" color="error">
              <Trash2 size={16} />
            </IconButton>
          </Popconfirm>
        </>
      )}

      {campaign.status === 'IN_PROGRESS' && (
        <Popconfirm message="Cancel campaign? This will stop sending messages" onConfirm={cancelCampaign}>
          <IconButton size="small" color="warning">
            <Ban size={16} />
          </IconButton>
        </Popconfirm>
      )}

      {(campaign.status === 'COMPLETED' || campaign.status === 'FAILED' || campaign.status === 'CANCELLED') && (
        <Popconfirm message="Delete campaign? This action cannot be undone" onConfirm={deleteCampaign}>
          <IconButton size="small" color="error">
            <Trash2 size={16} />
          </IconButton>
        </Popconfirm>
      )}
    </div>
  );
});

CampaignActionsColumn.displayName = 'CampaignActionsColumn';
